import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { asyncScheduler, catchError, exhaustMap, filter, finalize, from, map, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { checkUrlForAiProxy, checkUrlForProxy } from '../../functions/check-url-for-proxy';
import { Dialog } from '@angular/cdk/dialog';
import {
  ElectronicSignatureDialogComponent,
  ElectronicSignatureHeader
} from '../../../shared/components/electronic-signature-dialog/electronic-signature-dialog.component';
import { AlertService } from '../../../features/bizzmine/alerts/alert.service';
import { DialogModalComponent } from '../../../shared/components/modals/dialog-modal/dialog-modal.component';
import { DialogModalButtons } from '../../../shared/enums/dialog-modal-buttons';
import { DialogModalModelMode } from '../../../shared/components/modals/dialog-modal/dialog-modal-model';
import { TranslationService } from '../../services/translation/translation.service';
import { formsActions } from '../../../store/features/forms/forms-actions';
import { Store } from '@ngrx/store';
import {
  AiFirstTimeDialogComponent,
  AiFirstTimeHeader
} from '../../../shared/components/ai-first-time-dialog/ai-first-time-dialog.component';
import { PopUpModalType } from '../../../shared/enums/popup-modal-type';
import { PwaService } from '../../services/pwa/pwa.service';
import { AuthService } from '../../services/auth/auth.service';
import { BizzMineSessionStorageService } from '../../../shared/services/localStorage/bizzmine-session-storage.service';
import { IndexedDBService } from '../../services/indexed-db/indexed-db.service';
import { appActions } from 'src/app/store/features/app/app-actions';
import { PwaRefreshTokenExpiredModalComponent } from 'src/app/shared/components/pwa-refresh-token-expired-modal/pwa-refresh-token-expired-modal.component';
import { selectTopForm } from 'src/app/store/features/forms/forms-selectors';
import { StoreCollectionForm } from 'src/app/store/features/forms/forms-state';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  public static ignoreDispatchFailedUrls: Array<string> = ['token', 'translations', 'BizzMineHub', 'reminder/popup', 'notification/popup', 'task/popup'];
  public static temporaryIgnoreDispatchFailedUrls: Array<string> = [];
  public fileMap = [];
  private alertService = inject(AlertService);
  private dialog = inject(Dialog);
  private translationService = inject(TranslationService);
  private store$ = inject(Store);
  private pwaService = inject(PwaService);
  private authService = inject(AuthService);
  private indexeddbService = inject(IndexedDBService);
  private sessionStorageService = inject(BizzMineSessionStorageService);
  private router = inject(Router);
  private pwaRefreshTokenExpiredModal: Subscription | null = null;

  public static addTemporaryIgnoreDispatchFailedUrls(url: string): void {
    ApiInterceptor.temporaryIgnoreDispatchFailedUrls = ApiInterceptor.temporaryIgnoreDispatchFailedUrls ?? [];
    ApiInterceptor.temporaryIgnoreDispatchFailedUrls.push(url);
  }

  public static isValidationResult(response: HttpErrorResponse | undefined): boolean {
    return response?.error != undefined && (response?.error['IsValid'] == false || response?.error['isValid'] == false) && response?.error?.Errors != undefined && Array.isArray(response?.error?.Errors) && response?.error?.Errors?.length > 0;
  }

  private ignoreErrorHandling = 'ignore-error-handling';
  public static appendIgnoreErrorHandlingHeader(httpHeaders: HttpHeaders): HttpHeaders {
    httpHeaders = httpHeaders.append('ignore-error-handling', 'true')
    return httpHeaders;
  }

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    let req: HttpRequest<unknown>;

    let bypassedRequest = request.clone({
      setHeaders: { 'ngsw-bypass': 'true' }
    });

    if (checkUrlForProxy(request.url)) {
      if (checkUrlForAiProxy(request.url)) {
        req = bypassedRequest.clone({ url: `${environment.apiAiUri}/${request.url}` });
      } else {
        req = bypassedRequest.clone({ url: `${environment.apiUri}/${request.url}` });
      }
    } else {
      req = bypassedRequest.clone();
    }

    return this.handleRequest(next, req, request.url);
  }

  private handleRequestPwaCache(requestUrl: string): Observable<HttpResponse<null>> {
    return from(this.indexeddbService.getData('dataStore', requestUrl)).pipe(
      switchMap((cachedResponse: any) => cachedResponse
        ? of(new HttpResponse({ status: 200, body: cachedResponse.data }))
        : of(new HttpResponse({ status: 503, body: null }))),
      catchError(() => of(new HttpResponse({ status: 503, body: null })))
    );
  }

  private handleRequest(next: HttpHandler, req: HttpRequest<unknown>, requestUrl: string): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('error', error);
        this.dispatchFailedFetchIfNeeded(error);
        if(!req.headers.has(this.ignoreErrorHandling)){
        switch (error.status) {
          case 0:
            // No Internet connection handle pwa
            if(this.pwaService.isPwa() && this.indexeddbService){
              return this.handleRequestPwaCache(requestUrl);
              //   .pipe(
              //   tap(response => {
              //     if (response.status === 503 && requestUrl.toLowerCase().includes('usersettings')) {
              //       this.router.navigate(['/offline']);
              //     }
              //   })
              // );
            }
            if (ApiInterceptor.isValidationResult(error)) {
              break;
            }
            this.alertService.setAlert({
              type: 'error',
              content: [error.error.route, error.error.message],
              title: this.translationService.translate('NoServerConnection'),
              icon: 'triangle-exclamation',
              dismissable: true,
              timed: false
            });
            // Stop the request
            return of();
          case 400:
            if (ApiInterceptor.isValidationResult(error)) {
              break;
            }
            if (error.error['error'] == 'invalid_grant') {
              if(this.pwaService.isPwa()){
                this.handlePwaLogout();
              }else{
                this.sessionStorageService.clear();
                this.authService.initLogin();
              }
              break;
            }
            // Bad Request => should be handled on a per request basis
            this.alertService.setAlert({
              type: 'error',
              content: [error.error.route, error.error.message],
              title: this.translationService.translate('BadRequest'),
              icon: 'triangle-exclamation',
              dismissable: true,
              timed: false
            });
            break;
          case 401:

           if(this.pwaService.isPwa() && this.indexeddbService && !this.pwaService.isOnline){
              return this.handleRequestPwaCache(requestUrl);
            }
            if (ApiInterceptor.isValidationResult(error)) {
              break;
            }
            this.alertService.setAlert({
              type: 'error',
              content: [],
              title: this.translationService.translate('AccessDenied'),
              icon: 'triangle-exclamation',
              dismissable: true,
              timed: false
            });
            break;
          case 403:
            if (ApiInterceptor.isValidationResult(error)) {
              break;
            }
            this.alertService.setAlert({
              type: 'error',
              content: [],
              title: this.translationService.translate('AccessDenied'),
              icon: 'triangle-exclamation',
              dismissable: true,
              timed: false
            });
            break;
          case 404:
            // Not Found
            break;
          case 405:
            // Method Not Allowed
            break;
          case 409:
            this.dialog.open(DialogModalComponent, {
              data: {
                mode: DialogModalModelMode.simple,
                title: 'FileInProcess',
                message: 'FileInProcessBody',
                confirmationtext: '',
                buttons: DialogModalButtons.Ok,
                showCancelButton: false
              }
            });
            break;
          case 410:
            this.dialog.open(DialogModalComponent, {
              data: {
                mode: DialogModalModelMode.simple,
                title: 'FileInProcess',
                message: 'FileAlreadyCheckedOut',
                confirmationtext: '',
                buttons: DialogModalButtons.Ok,
                showCancelButton: false
              }
            });
            break;
          case 412:
            // Precondition Failed
            // Electronic Signature required
            switch (error.error.PopUpModalType) {
              case PopUpModalType.ElectronicSignature:
                return this.handleSignature(req, next, error);
              case PopUpModalType.AiFirstTimeUse:
                return this.handleAiFirstTime(req, next, error);
              case PopUpModalType.AiTemporarilyUnavailable:
                return this.handleAiFirstTime(req, next, error);
              case PopUpModalType.AiContentFiltering:
                return this.handleAiFirstTime(req, next, error);
              case PopUpModalType.UsageCreditsExceeded:
                return this.handleAiFirstTime(req, next, error);
            }
            break;
          case 429:
            break;
          case 500:
            // Internal Server error
            this.alertService.setAlert({
              type: 'error',
              content: [error.error.route, error.error.message],
              title: this.translationService.translate('BadRequest'),
              icon: 'triangle-exclamation',
              dismissable: true,
              timed: false
            });
            break;
          case 504:
            // Gateway Timeout
            break;
        }
        }
        

        return new Observable<HttpEvent<unknown>>((subscriber) => {
          subscriber.error(error);
        });
      })
    );

  }

  private handlePwaLogout(): void {
    if(!this.pwaRefreshTokenExpiredModal){
      this.pwaRefreshTokenExpiredModal = this.dialog.open(PwaRefreshTokenExpiredModalComponent).closed.pipe(take(1)).subscribe({
        next: (reAuthenticate) => {
          if (reAuthenticate) {
            const offlineItems = localStorage.getItem("offlineItems");
            localStorage.clear();
            localStorage.setItem("offlineItems", offlineItems ?? "");
            sessionStorage.clear();
            this.authService.initLogin(true);
          }else{
            // continue using app offline
            this.store$.dispatch(appActions.offlineModeChanged({ isOnline: false }));
          }
          this.pwaRefreshTokenExpiredModal?.unsubscribe();
          this.pwaRefreshTokenExpiredModal = null;
        },
      });
  }
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public isIE(): RegExpMatchArray {
    return <RegExpMatchArray>window.navigator.userAgent.match(/(MSIE|Trident)/);
  }

  private dispatchFailedFetchIfNeeded(error: HttpErrorResponse): void {
    //@Guillaume this was generic onCatchError handler -> but not always in the context of forms
    // for now ive added the exception of Pending tasks -> but this should be done on failed effect within the store at some point..

    // setting *all* forms into loading false even when e.g notifications / pending task (while create) has failed
    if ((error.url?.toLocaleLowerCase()?.includes('pendingsteps') && error.status == 404) || ApiInterceptor.ignoreDispatchFailedUrls?.filter(segment => error.url?.toLocaleLowerCase()?.includes(segment))?.length > 0 || ApiInterceptor.temporaryIgnoreDispatchFailedUrls?.filter(segment => error.url?.toLocaleLowerCase()?.includes(segment))?.length > 0) {
      return;
    }
    this.store$.dispatch(formsActions.failedFetch({}));
  }

  private cancelTopForm(formData: StoreCollectionForm): void {
    this.store$.dispatch(formsActions.formCanceled({
      formId: formData.id,
      form: formData.data,
      unsavedChanges: false,
      performFileCheckoutValidation: false,
      ignoreReroute: false
    }));
  }

  private handleSignatureCancel(request: HttpRequest<unknown>, topForm: StoreCollectionForm): void {
    //getFormByTaskIdOnBehalfOf api/task/${taskID}/collection/${collectionsID}/onbehalf/${assignedToID}/type/${assignedToType}/form
    //getFormByTaskID api/task/${taskID}/form
    //getFormByInstance api/collection/${collectionId}/instance/${instanceId}/version/${versionId}
    const url = new URL(request.url);
    const segments = url.pathname.split('/');
    const isOnBehalf = segments.findIndex(segment => segment === 'onbehalf') != -1;
    const isTask = segments.findIndex(segment => segment === 'task') != -1;
    const isCreate = segments.findIndex(segment => segment === 'create') != -1;

    const collection = segments.findIndex(segment => segment === 'collection');

    if (isCreate) {
      // Not needed, here to prevent create getting caught in the other if statements
    } else if (isTask) {
      const task = segments.findIndex(segment => segment === 'task');
      if (collection != -1 && task != -1) {
        if (Number(segments[collection + 1]) == topForm.data.CollectionsID &&
          Number(segments[task + 1]) == topForm.data.TasksID
        )
          this.cancelTopForm(topForm);
      }
    } else if (isOnBehalf && topForm.data.IsExecuteOnBehalfOf) {
      const task = segments.findIndex(segment => segment === 'task');
      const onBehalf = segments.findIndex(segment => segment === 'onbehalf');
      if (collection != -1 && task != -1) {
        if (Number(segments[collection + 1]) == topForm.data.CollectionsID &&
          Number(segments[task + 1]) == topForm.data.TasksID &&
          Number(segments[onBehalf + 1]) == topForm.data.AssignedToID
        )
          this.cancelTopForm(topForm);
      }
    } else {
      const instance = segments.findIndex(segment => segment === 'instance');
      const version = segments.findIndex(segment => segment === 'version');
      if (collection != -1 && instance != -1 && version != -1) {
        if (Number(segments[collection + 1]) == topForm.data.CollectionsID &&
          Number(segments[instance + 1]) == topForm.data.InstancesID &&
          Number(segments[version + 1]) == topForm.data.VersionsID
        )
          this.cancelTopForm(topForm);
      }
    }
  }

  private handleSignature(request: HttpRequest<unknown>, next: HttpHandler, error: HttpErrorResponse, previousDialogRef: ElectronicSignatureDialogComponent | null = null): Observable<HttpEvent<any>> {
    const ref = this.dialog.open<Array<ElectronicSignatureHeader>>(ElectronicSignatureDialogComponent, {
      data: {
        request: request,
        next: next,
        error: error
      }
    });
    ref.closed.subscribe((result: any) => {
      console.debug(`E signature closed with: ${result}`);
      const topForm = this.store$.selectSignal(selectTopForm())();
      if (result == 'cancelled' && topForm != undefined) {
        this.handleSignatureCancel(request, topForm);
      }
    });

    const electronicSignatureDialogComponent = (ref.componentInstance as ElectronicSignatureDialogComponent);
    return electronicSignatureDialogComponent.newRequest$.pipe(
      tap(() => {
        if (previousDialogRef) {
          asyncScheduler.schedule(() => {
            previousDialogRef.close();
          });
        }
      }),
      filter(httpRequest => httpRequest != null),
      map(httpRequest => httpRequest as HttpRequest<unknown>),
      exhaustMap(result => {
        return next.handle(result)
          .pipe(
            catchError((newError: HttpErrorResponse) => {
              if (newError.status == 412) {
                return this.handleSignature(request, next, newError, electronicSignatureDialogComponent);
              } else {
                throw newError;
              }
            }),
            finalize(() => {
              asyncScheduler.schedule(() => {
                electronicSignatureDialogComponent.isLoading = false;
                electronicSignatureDialogComponent.close();
              });
            }
            ));
      }));
  }

  private handleAiContentFilter(request: HttpRequest<unknown>, next: HttpHandler, error: HttpErrorResponse, previousDialogRef: AiFirstTimeDialogComponent | null = null): void {
    const ref = this.dialog.open<Array<AiFirstTimeHeader>>(AiFirstTimeDialogComponent, {
      data: {
        request: request,
        next: next,
        error: error
      }
    });
  }


  private handleAiFirstTime(request: HttpRequest<unknown>, next: HttpHandler, error: HttpErrorResponse, previousDialogRef: AiFirstTimeDialogComponent | null = null): Observable<HttpEvent<any>> {
    const ref = this.dialog.open<Array<AiFirstTimeHeader>>(AiFirstTimeDialogComponent, {
      data: {
        request: request,
        next: next,
        error: error
      }
    });
    const aiFirstTimeDialogComponent = (ref.componentInstance as AiFirstTimeDialogComponent);

    return aiFirstTimeDialogComponent.newRequest$.pipe(
      tap(() => {
        if (previousDialogRef) {
          asyncScheduler.schedule(() => {
            previousDialogRef.close();
          });
        }
      }),
      filter(httpRequest => httpRequest != null),
      map(httpRequest => httpRequest as HttpRequest<unknown>),
      exhaustMap(result => {
        return next.handle(result)
          .pipe(
            catchError((newError: HttpErrorResponse) => {
              return this.handleAiFirstTime(request, next, newError, aiFirstTimeDialogComponent);
            }),
            finalize(() => {
              asyncScheduler.schedule(() => {
                aiFirstTimeDialogComponent.isLoading = false;
                aiFirstTimeDialogComponent.close();
              });
            }
            ));
      }));
  }
}
